import { cn } from '@/shared/lib/cn';

/**
 * Foto do usuário, com a inicial do nome como fallback.
 *
 * ⚠️ A inicial NÃO é placeholder de carregamento: é o estado normal de quem
 * nunca enviou foto, e continua valendo se a URL assinada expirar. Por isso
 * `fotoUrl` é opcional em toda resposta que traz avatar, e nenhuma tela deve
 * reservar espaço vazio esperando a imagem.
 *
 * O `alt` é vazio de propósito: o nome já aparece ao lado em todos os usos, e
 * um `alt` com o nome faria o leitor de tela repeti-lo duas vezes.
 */
/**
 * Só a APARÊNCIA do fallback muda entre as duas: `solida` é o avatar do
 * header, `suave` é o da escolha do especialista, que vive sobre um
 * card já colorido e ficaria pesado em brand cheio. Com foto, as duas são
 * idênticas.
 */
type Variante = 'solida' | 'suave';

const FALLBACK: Record<Variante, string> = {
  solida: 'bg-brand text-brand-foreground',
  suave: 'bg-brand/15 text-brand',
};

export function Avatar({
  fotoUrl,
  nome,
  className,
  variante = 'solida',
  iniciais = 1,
}: {
  fotoUrl?: string | null;
  nome?: string | null;
  className?: string;
  variante?: Variante;
  /** Quantas letras no fallback. 2 nos cards maiores, onde 1 fica solta. */
  iniciais?: 1 | 2;
}) {
  const base = cn('rounded-full shrink-0 w-8 h-8', className);

  if (fotoUrl) return <img src={fotoUrl} alt="" className={cn(base, 'object-cover')} />;

  return (
    <div
      aria-hidden="true"
      className={cn(
        base,
        FALLBACK[variante],
        'flex items-center justify-center font-semibold text-xs uppercase',
      )}
    >
      {iniciaisDe(nome, iniciais)}
    </div>
  );
}

/** Exportada para quem monta o próprio círculo de avatar fora deste componente (ex.: `CampoDeFoto`). */
export function iniciaisDe(nome: string | null | undefined, quantas: 1 | 2 = 1): string {
  const limpo = nome?.trim();
  if (!limpo) return 'U';

  return limpo.slice(0, quantas).toLocaleUpperCase('pt-BR');
}
